import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Sparkles, Loader2 } from 'lucide-react';
import ThemeSelector from './ThemeSelector';
import toast from 'react-hot-toast';
import api from '../../../api';
import { getUserFriendlyError, ERROR_MESSAGES } from '../../../utils/errorMessages';

const SettingsPanel = ({
  selectedQuestion,
  formSettings,
  onFormSettingChange,
  onQuestionSettingChange,
  onAIImport,
  isMobile = false,
  onClose = () => {}
}) => {
  const themeColors = ['#0066FF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  // AI Import state
  const [aiText, setAiText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Handle AI question generation
  const handleGenerateQuestions = async () => {
    if (!aiText.trim()) {
      toast.error(ERROR_MESSAGES.AI.NO_TEXT_PROVIDED);
      return;
    }

    setIsGenerating(true);
    try {
      const response = await api.post('/forms/ai/generate-questions', {
        pastedText: aiText
      }, {
        timeout: 600000 // 60 seconds timeout for AI generation
      });

      if (response.success && response.data.questions) {
        onAIImport(response.data.questions);
        setAiText(''); // Clear textarea
        toast.success(`✨ Imported ${response.data.questions.length} questions!`);
      } else {
        toast.error(ERROR_MESSAGES.AI.GENERATION_FAILED);
      }
    } catch (error) {
      console.error('AI generation error:', error);
      const friendlyMessage = getUserFriendlyError(error, ERROR_MESSAGES.AI.GENERATION_FAILED);
      toast.error(friendlyMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  // Toggle component with better accessibility
  const Toggle = ({ enabled, onChange, label }) => (
    <div className="flex items-center justify-between py-1">
      <label className="text-sm font-medium text-gray-700 cursor-pointer">
        {label}
      </label>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative w-11 h-6 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          enabled ? 'bg-blue-500' : 'bg-gray-300'
        }`}
        role="switch"
        aria-checked={enabled}
        aria-label={label}
      >
        <span
          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-300 border border-gray-200 ${
            enabled ? 'left-6' : 'left-1'
          }`}
        />
      </button>
    </div>
  );

  // Custom Dropdown component with better state management
  const CustomDropdown = ({ value, onChange, options, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find(opt => opt.value === value) || options[0];

    return (
      <div className="relative" ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400 flex items-center justify-between group"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="text-gray-900">{selectedOption?.label}</span>
          <ChevronDown
            className={`w-4 h-4 text-gray-500 transition-transform duration-300 group-hover:text-gray-700 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-2.5 text-sm text-left transition-all duration-150 hover:bg-gray-50 ${
                  value === option.value
                    ? 'bg-blue-50 text-blue-600 font-medium'
                    : 'text-gray-700'
                }`}
                role="option"
                aria-selected={value === option.value}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Enhanced question type options
  const questionTypeOptions = [
    { value: 'short-text', label: 'Short Text' },
    { value: 'long-text', label: 'Long Text' },
    { value: 'multiple-choice', label: 'Multiple Choice' },
    { value: 'checkbox', label: 'Checkboxes' },
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'yes-no', label: 'Yes/No' },
    { value: 'rating', label: 'Rating' },
    { value: 'scale', label: 'Linear Scale' },
    { value: 'ranking', label: 'Ranking' },
    { value: 'email', label: 'Email' },
    { value: 'number', label: 'Number' },
    { value: 'phone', label: 'Phone' },
    { value: 'url', label: 'Website URL' },
    { value: 'date', label: 'Date' },
    { value: 'time', label: 'Time' },
    { value: 'gender', label: 'Gender' },
    { value: 'image', label: 'Image Choice' }
  ];

  const animationOptions = [
    { value: 'fade-in', label: 'Fade In' },
    { value: 'slide-up', label: 'Slide Up' },
    { value: 'zoom-in', label: 'Zoom In' },
    { value: 'bounce', label: 'Bounce' },
    { value: 'none', label: 'None' }
  ];

  // Handle number input changes safely
  const handlePointsChange = (value) => {
    const points = parseInt(value) || 0;
    onQuestionSettingChange('points', Math.max(0, points));
  };

  // Settings Content
  const settingsContent = selectedQuestion ? (
    <div className="space-y-6" data-tour="question-settings">
      {/* Question Type */}
      <CustomDropdown
        label="Question Type"
        value={selectedQuestion.type}
        onChange={(val) => onQuestionSettingChange('type', val)}
        options={questionTypeOptions}
      />

      {/* Required Toggle */}
      <Toggle
        enabled={selectedQuestion.required || false}
        onChange={(val) => onQuestionSettingChange('required', val)}
        label="Required Question"
      />

      {/* Points (for gamification) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Points Value
        </label>
        <input
          type="number"
          value={selectedQuestion.points || 0}
          onChange={(e) => handlePointsChange(e.target.value)}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
          min="0"
          max="1000"
          step="1"
        />
        <p className="text-xs text-gray-500 mt-1">
          XP earned when this question is answered
        </p>
      </div>

      {/* Animation */}
      <CustomDropdown
        label="Entrance Animation"
        value={selectedQuestion.animation || 'fade-in'}
        onChange={(val) => onQuestionSettingChange('animation', val)}
        options={animationOptions}
      />

      {/* Question ID (for debugging) */}
      <div className="pt-4 border-t border-gray-200">
        <label className="block text-xs font-medium text-gray-500 mb-1">
          Question ID
        </label>
        <code className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded block truncate">
          {selectedQuestion.id}
        </code>
      </div>
    </div>
  ) : (
    <div className="space-y-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-6">
        Form Settings
      </h3>
      <div className="border-t border-gray-200 mb-6" />

      {/* AI Import Section */}
      <div className="space-y-4" data-tour="ai-import">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-blue-600" />
          <h4 className="text-sm font-medium text-gray-700">
            AI-Powered Import
          </h4>
        </div>

        <p className="text-xs text-gray-500 leading-relaxed">
          Paste your questions below and Fylo will auto-create your form
        </p>

        <textarea
          className="w-full h-32 px-3 py-2.5 text-sm border-2 border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-300"
          placeholder="Paste your questions here..."
          value={aiText}
          onChange={(e) => setAiText(e.target.value)}
          disabled={isGenerating}
        />

        <button
          onClick={handleGenerateQuestions}
          disabled={!aiText.trim() || isGenerating}
          className="w-full px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-medium text-sm hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 "
        >
          {isGenerating ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Generating...
            </>
          ) : (
            <>
              
              Generate Questions
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Return Mobile Drawer or Desktop Sidebar
  return (
    <>
      {/* Mobile Drawer */}
      {isMobile && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 xl:hidden"
            onClick={onClose}
          />

          {/* Drawer */}
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl sm:rounded-t-3xl z-50 xl:hidden max-h-[85vh] sm:max-h-[80vh] overflow-y-auto transform transition-transform duration-300 translate-y-0 shadow-2xl">
            {/* Drawer Handle */}
            <div className="sticky top-0 bg-white pt-3 pb-2 px-4 sm:px-6 border-b border-gray-200 z-10">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-3" />
              <div className="flex items-center justify-between">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  {selectedQuestion ? 'Question Settings' : 'Form Settings'}
                </h3>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {selectedQuestion && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded mt-2 inline-block">
                  {selectedQuestion.type.replace('-', ' ')}
                </span>
              )}
            </div>
            <div className="p-4 sm:p-6 pb-20">
              {settingsContent}
            </div>
          </div>
        </>
      )}

      {/* Desktop Sidebar */}
      <aside className="w-80 bg-white border-l border-gray-200 overflow-y-auto hidden xl:block">
        <div className="p-6">
          {selectedQuestion && (
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sm font-semibold text-gray-900">
                Question Settings
              </h3>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                {selectedQuestion.type.replace('-', ' ')}
              </span>
            </div>
          )}
          {settingsContent}
        </div>
      </aside>
    </>
  );
};

export default SettingsPanel;
